import { ArrowRight, Sale } from '@solar-icons/react';
import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { Offcanvas, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import FreeDelivery from '~/components/checkout/FreeDelivery';
import ItemCountPill from '~/components/checkout/ItemCountPill';
import DropcartItem from '~/components/layout/header/DropcartItem';
import DropcartProductSlider from '~/components/layout/header/DropcartProductSlider';
import { useDropcart } from '~/context/DropcartContext';
import { useOrder } from '~/context/OrderContext';
import { pushViewCart, pushRemoveFromCart } from '~/gtm';
import { useRoutePath } from '~/hooks/useRoutePath';
import type { OrderItem } from '~/types/Order';
import { useFormatPrice } from '~/utils/price';
import { useDefaultCurrency } from '~/utils/useChannelConfig';

const DropcartOffcanvas: React.FC = () => {
  const { t } = useTranslation();
  const routePath = useRoutePath();
  const { isOpen, closeDropcart } = useDropcart();
  const { order, isFetching, updateOrderItem, removeOrderItem } = useOrder();
  const formatPrice = useFormatPrice();
  const defaultCurrency = useDefaultCurrency();

  const items = useMemo(() => order?.items ?? [], [order?.items]);
  const currencyCode = order?.currencyCode ?? defaultCurrency;
  const itemsTotal = order?.productsTotal ?? 0;
  const priceRemainingForFreeDelivery = order?.priceRemainingForFreeDelivery;
  // Mirrors FreeDelivery's own render conditions, so the spacing below it is only
  // reserved when the bar is actually there.
  const showFreeDelivery =
    order?.displayFreeDelivery !== false && (!!priceRemainingForFreeDelivery || priceRemainingForFreeDelivery === 0);

  // Track whether view_cart was already pushed for this open
  const viewCartPushedRef = useRef(false);

  // GTM: Push view_cart when dropcart opens (wait for fetch to complete)
  useEffect(() => {
    if (isOpen && order && !isFetching && !viewCartPushedRef.current) {
      pushViewCart(order);
      viewCartPushedRef.current = true;
    }
    if (!isOpen) {
      viewCartPushedRef.current = false;
    }
  }, [isOpen, order, isFetching]);

  // GTM: Wrap removeOrderItem to track remove_from_cart
  const handleRemoveItem = useCallback(
    (itemId: number) => {
      // Find the item being removed to track it
      const itemToRemove = items.find((item) => item.id === itemId);
      if (itemToRemove) {
        pushRemoveFromCart(itemToRemove as OrderItem, currencyCode);
      }
      removeOrderItem(itemId);
    },
    [items, currencyCode, removeOrderItem]
  );

  return (
    <Offcanvas show={isOpen} onHide={closeDropcart} placement="end" className="dropcart-offcanvas">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="p-2 fw-bold">{t('components.dropcart.title')}</Offcanvas.Title>
        <ItemCountPill count={items.length} className="checkout-header__pill--xs" />
      </Offcanvas.Header>
      <Offcanvas.Body>
        <FreeDelivery
          remainingAmount={priceRemainingForFreeDelivery}
          currencyCode={currencyCode}
          orderTotal={order?.total}
          onLinkClick={closeDropcart}
          display={order?.displayFreeDelivery}
        />
        {items.length === 0 ? (
          isFetching ? (
            <div className="dropcart-empty mt-4 d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div className="dropcart-empty mt-4">
              <p>{t('components.dropcart.empty')}</p>
              <Link to={routePath('homepage')} className="btn btn-primary" onClick={closeDropcart}>
                {t('components.dropcart.continue_shopping')}
              </Link>
            </div>
          )
        ) : (
          <div className={`dropcart-items-wrapper position-relative ${showFreeDelivery ? 'mt-4' : ''}`}>
            {isFetching && (
              <div className="dropcart-loading-overlay">
                <Spinner animation="border" variant="primary" />
              </div>
            )}
            <div className="dropcart-items border-bottom">
              {items.map((item) => (
                <DropcartItem
                  key={item.id}
                  item={item}
                  currencyCode={currencyCode}
                  onQuantityChange={updateOrderItem}
                  onRemove={handleRemoveItem}
                />
              ))}
              {order?.promotionCoupon && order?.orderPromotionTotal && (
                <div className="dropcart-item dropcart-item--discount">
                  <div className="dropcart-item__image dropcart-item__image--discount">
                    <Sale weight="Outline" size={64} />
                  </div>
                  <div className="dropcart-item__content">
                    <div className="dropcart-item__name">
                      <span className="fw-semibold">{order.promotionCoupon.code}</span>
                    </div>
                    <div className="dropcart-item__price-wrapper dropcart-item__price-wrapper--coupon text-end">
                      <div className="dropcart-item__price fw-bold">
                        {formatPrice(order.orderPromotionTotal, currencyCode)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <DropcartProductSlider />
      </Offcanvas.Body>
      {items.length > 0 && (
        <div className="offcanvas-footer">
          <div className="pt-3 d-flex justify-content-between align-items-center flex-wrap border-top">
            <span className="h5 fw-bold mb-0">{t('components.dropcart.total')}</span>
            <strong className="h5 fw-bold mb-0">{formatPrice(itemsTotal, currencyCode)}</strong>
          </div>
          <p className="text-body-tertiary">{t('components.dropcart.shipping_note')}</p>
          <div className="dropcart-buttons">
            <Link to={routePath('cart')} className="btn btn-primary" onClick={closeDropcart}>
              {t('components.dropcart.view_cart')} <ArrowRight size={22} />
            </Link>
          </div>
        </div>
      )}
    </Offcanvas>
  );
};

export default DropcartOffcanvas;
